/**
 * Transaction Action Modals
 * Provides modal dialogs for transaction bulk actions
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { CalendarIcon, ChevronsUpDown, Check, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { cn, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { type Transaction } from "@/types/transaction";
import { useUsersList } from "@/hooks/useUsersData";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Types
interface TransactionActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTransactions: Transaction[];
}

interface CreateCollectionCaseModalProps extends TransactionActionModalProps {
  onSave?: (data: CollectionCaseFormData) => void;
}

interface CreatePromiseModalProps extends TransactionActionModalProps {
  onSave?: (data: PromiseFormData) => void;
}

interface CreateDisputeModalProps extends TransactionActionModalProps {
  onSave?: (data: DisputeFormData) => void;
}

interface AddNoteModalProps extends TransactionActionModalProps {
  onSave?: (data: NoteFormData) => void;
}

export interface CollectionCaseFormData {
  customerIds: string[];
  priority: 'High' | 'Medium' | 'Low';
  strategyId: string;
  assignedTo?: string;
  notes: string;
  nextAction: string;
  nextActionDate: Date;
  transactionIds: number[];
}

export interface PromiseFormData {
  customerId: string;
  promiseDate: Date;
  amount: number;
  method: string;
  notes: string;
  sendConfirmation: boolean;
  transactionIds: number[];
}

export interface DisputeFormData {
  customerId: string;
  disputeTypeId: string;
  amount: number;
  priority: string;
  description: string;
  transactionIds: number[];
}

export interface NoteFormData {
  customerIds: string[];
  subject: string;
  content: string;
  category: string;
  isImportant: boolean;
  transactionIds: number[];
}

/**
 * Create Collection Case Modal
 */
export function CreateCollectionCaseModal({
  open,
  onOpenChange,
  selectedTransactions,
  onSave,
}: CreateCollectionCaseModalProps) {
  const { data: users = [] } = useUsersList();
  const activeUsers = users.filter(user => user.status === 'active');

  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [strategyId, setStrategyId] = useState('strategy-1');
  const [assignedTo, setAssignedTo] = useState<string>('unassigned');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('Follow-up call');
  const [nextActionDate, setNextActionDate] = useState<Date>(new Date());

  // Safety check - moved after all hooks
  if (!selectedTransactions || selectedTransactions.length === 0) {
    return null;
  }

  // Available collection strategies
  const availableStrategies = [
    { id: "strategy-1", name: "Standard Collection", type: "Standard" },
    { id: "strategy-2", name: "Intensive Follow-up", type: "Intensive" },
    { id: "strategy-3", name: "VIP Treatment", type: "VIP" },
    { id: "strategy-4", name: "High Risk Protocol", type: "High Risk" },
  ];

  // Get unique customers from selected transactions
  const uniqueCustomers = Array.from(
    new Map(
      selectedTransactions.map(t => [
        t.customerId, 
        { id: t.customerId, name: t.customerName || 'Unknown Customer' }
      ])
    ).values()
  );

  // Calculate total amount
  const totalAmount = selectedTransactions.reduce((sum, t) => sum + (t.outstandingAmount || t.amount || 0), 0);
  const currency = selectedTransactions[0]?.currency || 'GBP';

  const handleSave = () => {
    // Validation
    if (!strategyId) {
      toast.error("Please select a collection strategy");
      return;
    }
    if (!nextAction.trim()) {
      toast.error("Please enter a next action");
      return;
    }

    const formData: CollectionCaseFormData = {
      customerIds: uniqueCustomers.map(c => c.id.toString()),
      priority,
      strategyId,
      assignedTo: assignedTo === 'unassigned' ? undefined : assignedTo,
      notes,
      nextAction,
      nextActionDate,
      transactionIds: selectedTransactions.map(t => t.id),
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success(`Collection case${uniqueCustomers.length > 1 ? 's' : ''} created successfully`);
    }

    // Reset form
    setPriority('Medium');
    setStrategyId('strategy-1');
    setAssignedTo('unassigned');
    setNotes('');
    setNextAction('Follow-up call');
    setNextActionDate(new Date());
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[600px] bg-white dark:bg-gray-950 overflow-hidden">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Create Collection Case</SheetTitle>
          <SheetDescription>
            Create collection case{uniqueCustomers.length > 1 ? 's' : ''} for {selectedTransactions.length} selected transaction{selectedTransactions.length > 1 ? 's' : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4">
          <div className="grid gap-6 py-6">
            {/* Summary */}
          <div className="space-y-2">
            <Label>Summary</Label>
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Transactions:</span>
                <span className="font-medium">{selectedTransactions.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Customers:</span>
                <span className="font-medium">{uniqueCustomers.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Amount:</span>
                <span className="font-semibold">{formatCurrency(totalAmount, currency)}</span>
              </div>
            </div>
          </div>

          {/* Customer List */}
          {uniqueCustomers.length > 1 && (
            <div className="space-y-2">
              <Label>Customers ({uniqueCustomers.length})</Label>
              <div className="rounded-lg border p-3 max-h-32 overflow-y-auto">
                <div className="space-y-1">
                  {uniqueCustomers.map(customer => (
                    <div key={customer.id} className="text-sm">
                      {customer.name}
                    </div>
                  ))}
                </div>
              </div>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  A separate collection case will be created for each customer
                </AlertDescription>
              </Alert>
            </div>
          )}

          {uniqueCustomers.length === 1 && (
            <div className="space-y-2">
              <Label>Customer</Label>
              <Input value={uniqueCustomers[0].name} disabled />
            </div>
          )}

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority *</Label>
            <Select value={priority} onValueChange={(value: any) => setPriority(value)}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Collection Strategy */}
          <div className="space-y-2">
            <Label htmlFor="strategy">Collection Strategy *</Label>
            <Select value={strategyId} onValueChange={setStrategyId}>
              <SelectTrigger id="strategy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableStrategies.map(strategy => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Assigned To */}
          <div className="space-y-2">
            <Label htmlFor="assigned-to">Assign To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger id="assigned-to">
                <SelectValue placeholder="Select user (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {activeUsers.map(user => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Next Action */}
          <div className="space-y-2">
            <Label htmlFor="next-action">Next Action *</Label>
            <Input
              id="next-action"
              placeholder="e.g., Follow-up call, Send reminder email"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
            />
          </div>

          {/* Next Action Date */}
          <div className="space-y-2">
            <Label htmlFor="next-action-date">Next Action Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="next-action-date"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !nextActionDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {nextActionDate ? format(nextActionDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={nextActionDate}
                  onSelect={(date) => date && setNextActionDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          </div>
        </div>

        <SheetFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Create Case{uniqueCustomers.length > 1 ? 's' : ''}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Create Promise to Pay Modal
 */
export function CreatePromiseModal({
  open,
  onOpenChange,
  selectedTransactions,
  onSave,
}: CreatePromiseModalProps) {
  const [promiseDate, setPromiseDate] = useState<Date>();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [sendConfirmation, setSendConfirmation] = useState(true);

  // Check if all transactions are from the same customer
  const uniqueCustomers = Array.from(
    new Set(selectedTransactions.map(t => t.customerId))
  );
  const isSingleCustomer = uniqueCustomers.length === 1;
  const customerName = isSingleCustomer ? selectedTransactions[0]?.customerName : '';
  
  // Calculate total outstanding
  const totalOutstanding = selectedTransactions.reduce((sum, t) => sum + (t.outstandingAmount || t.amount), 0);
  const currency = selectedTransactions[0]?.currency || 'GBP';

  // Pre-fill amount with total outstanding
  useEffect(() => {
    if (open && !amount) {
      setAmount(totalOutstanding.toFixed(2));
    }
  }, [open, totalOutstanding]);

  const handleSave = () => {
    // Validation
    if (!isSingleCustomer) {
      toast.error("Promises can only be created for transactions from a single customer");
      return;
    }
    if (!promiseDate) {
      toast.error("Please select a promise date");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!method) {
      toast.error("Please select a payment method");
      return;
    }

    const formData: PromiseFormData = {
      customerId: uniqueCustomers[0].toString(),
      promiseDate,
      amount: parseFloat(amount),
      method,
      notes,
      sendConfirmation,
      transactionIds: selectedTransactions.map(t => t.id),
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success("Promise to pay recorded successfully");
    }

    // Reset form
    setPromiseDate(undefined);
    setAmount("");
    setMethod("");
    setNotes("");
    setSendConfirmation(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Promise to Pay</DialogTitle>
          <DialogDescription>
            Record a payment promise for {selectedTransactions.length} selected transaction{selectedTransactions.length > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        {!isSingleCustomer && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Promises can only be created for transactions from a single customer. 
              Your selection includes {uniqueCustomers.length} different customers.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input 
              id="customer" 
              value={isSingleCustomer ? customerName : `${uniqueCustomers.length} different customers`} 
              disabled 
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="transactions">Transactions</Label>
            <Input
              id="transactions"
              value={`${selectedTransactions.length} transaction${selectedTransactions.length > 1 ? 's' : ''} selected`}
              disabled
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="outstanding">Total Outstanding</Label>
            <Input
              id="outstanding"
              value={formatCurrency(totalOutstanding, currency)}
              disabled
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="promise-date">Promise Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="promise-date"
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !promiseDate && "text-muted-foreground"
                  )}
                  disabled={!isSingleCustomer}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {promiseDate ? format(promiseDate, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={promiseDate}
                  onSelect={setPromiseDate}
                  initialFocus
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="amount">Promise Amount *</Label>
            <Input
              id="amount"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={!isSingleCustomer}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="method">Payment Method *</Label>
            <Select value={method} onValueChange={setMethod} disabled={!isSingleCustomer}>
              <SelectTrigger id="method">
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="direct_debit">Direct Debit</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              disabled={!isSingleCustomer}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-confirmation"
              checked={sendConfirmation}
              onCheckedChange={(checked) => setSendConfirmation(checked as boolean)}
              disabled={!isSingleCustomer}
            />
            <Label
              htmlFor="send-confirmation"
              className="text-sm font-normal cursor-pointer"
            >
              Send confirmation email to customer
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isSingleCustomer}>
            Save Promise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Create Dispute Modal
 */
export function CreateDisputeModal({
  open,
  onOpenChange,
  selectedTransactions,
  onSave,
}: CreateDisputeModalProps) {
  const [disputeTypeId, setDisputeTypeId] = useState("");
  const [amount, setAmount] = useState("");
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");

  // Check if all transactions are from the same customer
  const uniqueCustomers = Array.from(
    new Set(selectedTransactions.map(t => t.customerId))
  );
  const isSingleCustomer = uniqueCustomers.length === 1;
  const customerName = isSingleCustomer ? selectedTransactions[0]?.customerName : '';
  
  // Calculate total amount
  const totalAmount = selectedTransactions.reduce((sum, t) => sum + (t.outstandingAmount || t.amount), 0);
  const currency = selectedTransactions[0]?.currency || 'GBP';

  // Dispute types
  const disputeTypes = [
    { id: "1", name: "Billing Error" },
    { id: "2", name: "Service Quality" },
    { id: "3", name: "Pricing Dispute" },
    { id: "4", name: "Delivery Issue" },
    { id: "5", name: "Product Quality" },
    { id: "6", name: "Contract Terms" },
    { id: "7", name: "Other" },
  ];

  // Pre-fill amount with total
  useEffect(() => {
    if (open && !amount) {
      setAmount(totalAmount.toFixed(2));
    }
  }, [open, totalAmount]);

  const handleSave = () => {
    // Validation
    if (!isSingleCustomer) {
      toast.error("Disputes can only be created for transactions from a single customer");
      return;
    }
    if (!disputeTypeId) {
      toast.error("Please select a dispute type");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid dispute amount");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    const formData: DisputeFormData = {
      customerId: uniqueCustomers[0].toString(),
      disputeTypeId,
      amount: parseFloat(amount),
      priority,
      description,
      transactionIds: selectedTransactions.map(t => t.id),
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success("Dispute created successfully");
    }

    // Reset form
    setDisputeTypeId("");
    setAmount("");
    setPriority("medium");
    setDescription("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Dispute</DialogTitle>
          <DialogDescription>
            Flag {selectedTransactions.length} transaction{selectedTransactions.length > 1 ? 's' : ''} as disputed
          </DialogDescription>
        </DialogHeader>

        {!isSingleCustomer && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Disputes can only be created for transactions from a single customer. 
              Your selection includes {uniqueCustomers.length} different customers.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input 
              id="customer" 
              value={isSingleCustomer ? customerName : `${uniqueCustomers.length} different customers`} 
              disabled 
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="transactions">Transactions</Label>
            <Input
              id="transactions"
              value={`${selectedTransactions.length} transaction${selectedTransactions.length > 1 ? 's' : ''} selected`}
              disabled
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="total-amount">Total Amount</Label>
            <Input
              id="total-amount"
              value={formatCurrency(totalAmount, currency)}
              disabled
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="dispute-type">Dispute Type *</Label>
            <Select value={disputeTypeId} onValueChange={setDisputeTypeId} disabled={!isSingleCustomer}>
              <SelectTrigger id="dispute-type">
                <SelectValue placeholder="Select dispute type" />
              </SelectTrigger>
              <SelectContent>
                {disputeTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="dispute-amount">Dispute Amount *</Label>
            <Input
              id="dispute-amount"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={!isSingleCustomer}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="priority">Priority *</Label>
            <Select value={priority} onValueChange={setPriority} disabled={!isSingleCustomer}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Describe the dispute..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              disabled={!isSingleCustomer}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isSingleCustomer}>
            Create Dispute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add Note Modal
 */
export function AddNoteModal({
  open,
  onOpenChange,
  selectedTransactions,
  onSave,
}: AddNoteModalProps) {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [isImportant, setIsImportant] = useState(false);

  // Get unique customers
  const uniqueCustomers = Array.from(
    new Map(
      selectedTransactions.map(t => [t.customerId, { id: t.customerId, name: t.customerName }])
    ).values()
  );

  const handleSave = () => {
    // Validation
    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!content.trim()) {
      toast.error("Please enter note content");
      return;
    }
    if (!category) {
      toast.error("Please select a category");
      return;
    }

    const formData: NoteFormData = {
      customerIds: uniqueCustomers.map(c => c.id.toString()),
      subject,
      content,
      category,
      isImportant,
      transactionIds: selectedTransactions.map(t => t.id),
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success(`Note${uniqueCustomers.length > 1 ? 's' : ''} added successfully`);
    }

    // Reset form
    setSubject("");
    setContent("");
    setCategory("");
    setIsImportant(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>
            Add a note for {selectedTransactions.length} selected transaction{selectedTransactions.length > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Summary */}
          <div className="space-y-2">
            <Label>Summary</Label>
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Transactions:</span>
                <span className="font-medium">{selectedTransactions.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Customers:</span>
                <span className="font-medium">{uniqueCustomers.length}</span>
              </div>
            </div>
          </div>

          {/* Customer List */}
          {uniqueCustomers.length > 1 && (
            <div className="space-y-2">
              <Label>Customers ({uniqueCustomers.length})</Label>
              <div className="rounded-lg border p-3 max-h-32 overflow-y-auto">
                <div className="space-y-1">
                  {uniqueCustomers.map(customer => (
                    <div key={customer.id} className="text-sm">
                      {customer.name}
                    </div>
                  ))}
                </div>
              </div>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  A note will be added to each customer's account
                </AlertDescription>
              </Alert>
            </div>
          )}

          {uniqueCustomers.length === 1 && (
            <div className="grid gap-2">
              <Label htmlFor="customer">Customer</Label>
              <Input id="customer" value={uniqueCustomers[0].name} disabled />
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              placeholder="Enter note subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="payment">Payment Discussion</SelectItem>
                <SelectItem value="dispute">Dispute</SelectItem>
                <SelectItem value="credit">Credit Review</SelectItem>
                <SelectItem value="collection">Collection Activity</SelectItem>
                <SelectItem value="account_review">Account Review</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="content">Note Content *</Label>
            <Textarea
              id="content"
              placeholder="Enter note content..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-important"
              checked={isImportant}
              onCheckedChange={(checked) => setIsImportant(checked as boolean)}
            />
            <Label htmlFor="is-important" className="text-sm font-normal cursor-pointer">
              Mark as important
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
